import React, { useState } from 'react';
import { CalendarClock, ChevronDown, ChevronUp, ClipboardCheck } from 'lucide-react';
import { PortalFristenAktePanel } from './verfahrensmotor/PortalFristenAktePanel';
import { PortalAktenwaechterPanel } from './PortalAktenwaechterPanel';

export interface PortalCollapsibleGuardStripProps {
  customerId: string;
  authToken?: string | null;
  akteNcPath?: string | null;
  completedByEmail?: string;
  className?: string;
}

/**
 * Kompakte, nebeneinander ausklappbare Fristen- und Aktenwächter-Panels im Editor-Split.
 */
export const PortalCollapsibleGuardStrip: React.FC<PortalCollapsibleGuardStripProps> = ({
  customerId,
  authToken,
  akteNcPath,
  completedByEmail,
  className = '',
}) => {
  const [fristenExpanded, setFristenExpanded] = useState(false);
  const [akteExpanded, setAkteExpanded] = useState(false);
  const [fristenSummary, setFristenSummary] = useState<string | null>(null);
  const [akteSummary, setAkteSummary] = useState<string | null>(null);
  const [fristenActive, setFristenActive] = useState(false);
  const [akteActive, setAkteActive] = useState(false);

  const showFristen = fristenActive || Boolean(akteNcPath?.trim());
  const showAkte = akteActive || Boolean(akteNcPath?.trim() || authToken?.trim());

  if (!showFristen && !showAkte) return null;

  const gridCols =
    showFristen && showAkte ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1';

  return (
    <div className={`mx-3 mb-2 grid gap-2 ${gridCols} ${className}`} aria-label="Fristen- und Aktenwächter">
      {showFristen ? (
        <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden min-w-0">
          <button
            type="button"
            onClick={() => setFristenExpanded((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03] transition-colors"
            aria-expanded={fristenExpanded}
          >
            <CalendarClock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex-shrink-0">
              Fristen
            </span>
            <span className="text-[11px] text-slate-300 truncate flex-1 min-w-0">
              {fristenSummary || 'Lade…'}
            </span>
            {fristenExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            )}
          </button>
          <div
            className={fristenExpanded ? 'border-t border-white/[0.06]' : 'sr-only'}
            aria-hidden={!fristenExpanded}
          >
            <PortalFristenAktePanel
              customerId={customerId}
              authToken={authToken}
              akteNcPath={akteNcPath}
              embedMode="strip"
              onCollapsedSummaryChange={(summary) => {
                setFristenSummary(summary);
                setFristenActive(summary !== null);
              }}
            />
          </div>
        </section>
      ) : null}

      {showAkte ? (
        <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden min-w-0">
          <button
            type="button"
            onClick={() => setAkteExpanded((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03] transition-colors"
            aria-expanded={akteExpanded}
          >
            <ClipboardCheck className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex-shrink-0">
              Akte
            </span>
            <span className="text-[11px] text-slate-300 truncate flex-1 min-w-0">
              {akteSummary || 'Lade…'}
            </span>
            {akteExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            )}
          </button>
          <div
            className={akteExpanded ? 'border-t border-white/[0.06]' : 'sr-only'}
            aria-hidden={!akteExpanded}
          >
            <PortalAktenwaechterPanel
              customerId={customerId}
              authToken={authToken}
              aktePath={akteNcPath}
              completedByEmail={completedByEmail}
              embedMode="strip"
              onCollapsedSummaryChange={(summary) => {
                setAkteSummary(summary);
                setAkteActive(summary !== null);
              }}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};
